import React, { Fragment, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

import Icon from "../../../components/Icon/Icon";
import { findLocationByCoordinates, getLocationId } from "../../../utils/score-insights/ai/aiLocationMatchers";

/**
 * ScoreInsightsMessageContent
 *
 * Shared message renderer used by BOTH:
 * - Live streaming (StreamingChat via ScoreInsightsAI)
 * - Saved replay (score-insights-layout rendering ai_suggestion_saved_response.assistant_content)
 *
 * Converts action markers into UI buttons:
 * - [ADD_TO_MAP:lat,lng] or [ADD_TO_MAP:lat,lng:score:note]
 * - [ADD_ALL_TO_MAP]
 *
 * Optionally enriches marker buttons with complete location objects
 * by matching coordinates against `suggestedLocations`.
 */
const ScoreInsightsMessageContent = ({
	content,
	messageIndex,
	suggestedLocations = [],
	onAddLocation,
	onAddAllLocations,
	renderLocationButton,
	renderAddAllButton,
	hasAiNote = false,
	ai_call_params = {},
	ai_used_on = "",
	addedAiLocations = [],
}) => {
	const parseMessageWithActions = useCallback(
		(text) => {
			const parts = [];
			let lastIndex = 0;

			const locationMarkerRegex =
				/\[ADD_TO_MAP:(?:_id:([a-f0-9]{24}):)?([-\d.]+),([-\d.]+)(?::([^:\]]+))?(?::([^\]]+))?\]/gi;

			const addAllMarkerRegex = /\[ADD_ALL_TO_MAP\]/g;

			let match;
			const matches = [];

			while ((match = locationMarkerRegex.exec(text)) !== null) {
				const locationId = match[1] || null;
				const lat = parseFloat(match[2]);
				const lng = parseFloat(match[3]);
				const score = match[4] ? parseFloat(match[4]) : null;
				const note = match[5] ? match[5].trim() : null;

				const completeLocation = findLocationByCoordinates(suggestedLocations, {
					_id: locationId,
					lat,
					lng,
				});

				matches.push({
					locationId,
					index: match.index,
					length: match[0].length,
					type: "location",
					lat,
					lng,
					score,
					note,
					completeLocation,
				});
			}

			const addAllMatch = addAllMarkerRegex.exec(text);
			if (addAllMatch) {
				matches.push({
					index: addAllMatch.index,
					length: addAllMatch[0].length,
					type: "addAll",
				});
			}

			matches.forEach((m, idx) => {
				if (m.index > lastIndex) {
					parts.push({
						type: "text",
						content: text.substring(lastIndex, m.index),
						key: `text-${messageIndex}-${idx}-${lastIndex}`,
					});
				}

				parts.push({
					type: "button",
					buttonType: m.type,
					locationId: m.locationId,
					lat: m.lat,
					lng: m.lng,
					score: m.score,
					note: m.note,
					completeLocation: m.completeLocation,
					key: `action-${messageIndex}-${idx}`,
				});
				lastIndex = m.index + m.length;
			});

			// Add remaining text
			if (lastIndex < text.length) {
				parts.push({
					type: "text",
					content: text.substring(lastIndex),
					key: `text-${messageIndex}-tail-${lastIndex}`,
				});
			}

			return parts;
		},
		[suggestedLocations, messageIndex],
	);

	const parts = useMemo(() => parseMessageWithActions(content || ""), [content, parseMessageWithActions]);

	const allLocationsAdded = useMemo(() => {
		const locationParts = parts.filter((part) => part.type === "button" && part.buttonType === "location");

		if (locationParts.length === 0) {
			return false;
		}

		return locationParts.every((part) => {
			const resolvedId = part.locationId || getLocationId(part.completeLocation);

			const isAdded =
				addedAiLocations.some((loc) => getLocationId(loc) === resolvedId) ||
				part.completeLocation?.is_added_from_ai === true;

			return isAdded;
		});
	}, [parts, addedAiLocations]);

	const defaultLocationButton = useCallback(
		({ locationId, lat, lng, completeLocation, isAdded, onClick }) => (
			<button
				className='btn theme-primary-outline btn-round'
				disabled={isAdded}
				onClick={() => {
					if (isAdded) return;

					onClick({
						_id: completeLocation?._id || locationId,
						location_id: completeLocation?.location_id || locationId,
						lat,
						lng,
						...completeLocation,
					});
				}}
			>
				<Icon theme={"primary"} color={"#5c86c1"} className={"border-0 m-0"} icon={"map-pin-3"} size={"sm"} />
				{isAdded ? "Location Already Added" : "Add location to my list"}
			</button>
		),
		[],
	);
	/**
	 * Default add all button renderer
	 */
	const defaultAddAllButton = useCallback(
		({ onClick, isDisabled }) => (
			<button
				className='btn btn-sm btn-outline-primary'
				disabled={isDisabled}
				onClick={() => {
					if (onClick && !isDisabled) {
						onClick();
					}
				}}
			>
				{isDisabled ? "All Locations Already Added" : "Add All AI Reccomended Locations"}
			</button>
		),
		[],
	);

	const LocationButton = renderLocationButton || defaultLocationButton;
	const AddAllButton = renderAddAllButton || defaultAddAllButton;

	const buttonStyle = { marginTop: "0.5rem", marginBottom: "0.5rem" };
	const addAllButtonStyle = { marginTop: "1rem", marginBottom: "1rem" };

	const aiWeights = useMemo(() => {
		if (!ai_call_params) return [];

		return Object.entries(ai_call_params)
			.filter(([key, value]) => key.startsWith("weight_") && typeof value === "number" && value !== 0)
			.map(([key, value]) => {
				const label = key
					.replace("weight_", "")
					.replace(/_/g, " ")
					.replace(/^\w/, (c) => c.toUpperCase());

				return { label, value };
			});
	}, [ai_call_params]);

	return (
		<div className='bluechip-ai'>
			{hasAiNote && (
				<Fragment>
					<h6 style={{ marginBottom: "12px" }} className='ai-note'>
						AI Replay
					</h6>
					<p style={{ marginBottom: "12px" }}>
						The following locations were recommended by AI based on dashboard settings on {ai_used_on}. The
						following weights were used to recommend the locations:
					</p>

					{aiWeights.length > 0 && (
						<Fragment>
							<p></p>
							<ul className='ai-replay-weights' style={{ marginBottom: "12px" }}>
								{aiWeights.map((w, idx) => (
									<li key={`ai-weight-${idx}`}>
										{w.label} : {w.value}%
									</li>
								))}
							</ul>
						</Fragment>
					)}
				</Fragment>
			)}
			<div className={`${hasAiNote ? "system-msg" : ""}`}>
				{parts.map((part, idx) => {
					if (part.type === "text") {
						return (
							<ReactMarkdown
								key={part.key || `text-${messageIndex}-${idx}`}
								rehypePlugins={[]}
								remarkPlugins={[remarkGfm]}
							>
								{part.content}
							</ReactMarkdown>
						);
					}

					if (part.type === "button" && part.buttonType === "location") {
						const resolvedId = part.locationId || getLocationId(part.completeLocation);

						const isAdded =
							addedAiLocations.some((loc) => getLocationId(loc) === resolvedId) ||
							part.completeLocation?.is_added_from_ai === true;
						return (
							<div key={part.key} style={buttonStyle}>
								<LocationButton
									locationId={resolvedId}
									lat={part.lat}
									lng={part.lng}
									completeLocation={part.completeLocation}
									isAdded={isAdded}
									onClick={onAddLocation}
								/>
							</div>
						);
					}

					if (part.type === "button" && part.buttonType === "addAll") {
						return (
							<div key={part.key} style={addAllButtonStyle}>
								<AddAllButton onClick={onAddAllLocations} isDisabled={allLocationsAdded} />
							</div>
						);
					}

					return null;
				})}
			</div>
		</div>
	);
};

export default ScoreInsightsMessageContent;
